import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { format, startOfWeek, endOfWeek } from 'date-fns';
import { businessToday, parseBusinessDay } from '@/lib/format';
import { supabase } from '@/integrations/supabase/client';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import { captureError } from "@/lib/logger";
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { UniversalDateRangePicker } from '@/components/UniversalDateRangePicker';
import { Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export type CollectionActionType = 'waiver' | 'note';

interface ExistingRecord {
    id: number;
    contract_id: number;
    period_start: string;
    period_end: string;
    reason: string | null;
}

interface CollectionActionSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    type: CollectionActionType;
    contractId: number;
    driverName: string;
    /** Default period dates from the Collections tab filter */
    defaultPeriod: { from: Date; to: Date };
    /** Existing record if editing */
    existingRecord?: ExistingRecord | null;
    /** Fixed period from instalment tile - makes date range non-editable */
    fixedPeriod?: { from: string; to: string } | null;
}

/**
 * `repayment_interest_waivers.period_start/period_end` (and the notes table's) ARE `date` COLUMNS,
 * and this sheet keeps them calendar strings end to end — 'yyyy-MM-dd' out of PostgREST, parsed
 * field by field for the picker, `format(…, 'yyyy-MM-dd')` back into the write.
 *
 * Read and write are one decision here, exactly as on the advance dossier's repayment area
 * (`setup/advance/areas/RepaymentArea.tsx`): `new Date('2026-08-01')` is
 * UTC midnight and `format` reads LOCAL parts, so west of Greenwich the picker already opened on
 * 2026-07-31 — and the save wrote that, so every open-and-save walked the waived period back a day
 * and kept walking. A waiver moved off its week stops cancelling the shortfall it was granted for.
 */
const calendarPeriod = (from: string, to: string) => ({
    from: parseBusinessDay(from) ?? businessToday(),
    to: parseBusinessDay(to) ?? businessToday(),
});

export function CollectionActionSheet({
    open,
    onOpenChange,
    type,
    contractId,
    driverName,
    defaultPeriod,
    existingRecord,
    fixedPeriod,
}: CollectionActionSheetProps) {
    const queryClient = useQueryClient();
    const isEditing = !!existingRecord;

    // Form state
    const [period, setPeriod] = useState<{ from: Date; to: Date }>(() => {
        if (existingRecord) {
            return calendarPeriod(existingRecord.period_start, existingRecord.period_end);
        }
        return {
            from: startOfWeek(defaultPeriod.from, { weekStartsOn: 1 }),
            to: endOfWeek(defaultPeriod.from, { weekStartsOn: 1 }),
        };
    });
    const [reason, setReason] = useState(existingRecord?.reason || '');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    // Reset form when sheet opens with new data
    useEffect(() => {
        if (open) {
            if (existingRecord) {
                setPeriod(calendarPeriod(existingRecord.period_start, existingRecord.period_end));
                setReason(existingRecord.reason || '');
            } else if (fixedPeriod) {
                // The instalment tile's own 'yyyy-MM-dd' week bounds, read the same way.
                setPeriod(calendarPeriod(fixedPeriod.from, fixedPeriod.to));
                setReason('');
            } else {
                setPeriod({
                    from: startOfWeek(defaultPeriod.from, { weekStartsOn: 1 }),
                    to: endOfWeek(defaultPeriod.from, { weekStartsOn: 1 }),
                });
                setReason('');
            }
        }
    }, [open, existingRecord, defaultPeriod, fixedPeriod]);

    const tableName = type === 'waiver' ? 'repayment_interest_waivers' : 'repayment_notes';
    const typeLabel = type === 'waiver' ? 'Waiver' : 'Note';

    // Save mutation (insert or update)
    // Pass recordId explicitly to avoid stale closure issues
    const saveMutation = useMutation({
        mutationFn: async ({ recordId, isUpdate }: { recordId: number | null; isUpdate: boolean }) => {
            const payload = {
                contract_id: contractId,
                period_start: format(period.from, 'yyyy-MM-dd'),
                period_end: format(period.to, 'yyyy-MM-dd'),
                reason: reason || null,
            };

            if (isUpdate && recordId) {
                const { error } = await supabase
                    .from(tableName)
                    .update(payload)
                    .eq('id', recordId);
                if (error) throw error;
            } else {
                const { error } = await supabase.from(tableName).insert(payload);
                if (error) throw error;
            }
        },
        onSuccess: () => {
            toast.success(`${typeLabel} ${isEditing ? 'updated' : 'added'} successfully`);
            queryClient.invalidateQueries({ queryKey: ['outstanding-contracts'] });
            queryClient.invalidateQueries({ queryKey: ['collection-actions'] });
            // Also invalidate interest-specific queries for Contract Insights Interest tab
            queryClient.invalidateQueries({ queryKey: ['contract-interest-waivers', contractId] });
            queryClient.invalidateQueries({ queryKey: ['contract-interest-notes', contractId] });
            onOpenChange(false);
        },
        onError: (error: Error) => {
            toast.error(error.message);
        },
    });

    // Delete mutation
    const deleteMutation = useMutation({
        mutationFn: async () => {
            if (!existingRecord) return;
            const { error } = await supabase
                .from(tableName)
                .delete()
                .eq('id', existingRecord.id);
            if (error) throw error;
        },
        onSuccess: () => {
            toast.success(`${typeLabel} deleted`);
            queryClient.invalidateQueries({ queryKey: ['outstanding-contracts'] });
            queryClient.invalidateQueries({ queryKey: ['collection-actions'] });
            // Also invalidate interest-specific queries for Contract Insights Interest tab
            queryClient.invalidateQueries({ queryKey: ['contract-interest-waivers', contractId] });
            queryClient.invalidateQueries({ queryKey: ['contract-interest-notes', contractId] });
            onOpenChange(false);
        },
        onError: (error: Error) => {
            toast.error(error.message);
        },
    });

    const handleSave = () => {
        // Validate: notes require a reason
        if (type === 'note' && !reason.trim()) {
            toast.error('Please enter a reason for this note');
            return;
        }
        // Validate: contract ID must match if editing
        if (isEditing && existingRecord?.contract_id !== contractId) {
            captureError(new Error('Contract ID mismatch!'), { extra: { recordContractId: existingRecord?.contract_id, propsContractId: contractId } });
            toast.error('Data mismatch detected. Please close and try again.');
            return;
        }
        // Pass current values explicitly to avoid stale closure issues
        saveMutation.mutate({
            recordId: existingRecord?.id || null,
            isUpdate: isEditing,
        });
    };

    const handleDelete = () => {
        setShowDeleteConfirm(false);
        deleteMutation.mutate();
    };

    const isPending = saveMutation.isPending || deleteMutation.isPending;

    return (
        <>
            <Sheet open={open} onOpenChange={onOpenChange}>
                <SheetContent
                    side="right"
                    hideOverlay
                    className="w-[440px] m-4 h-[calc(100vh-32px)] rounded-xl border flex flex-col"
                >
                    <SheetHeader className="mb-4">
                        <SheetTitle>
                            {isEditing ? `Edit ${typeLabel}` : `Add ${typeLabel}`}
                        </SheetTitle>
                    </SheetHeader>

                    <div className="flex-1 space-y-6">
                        {/* Contract Info */}
                        <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                            <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Driver</span>
                                <span className="font-medium">{driverName}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Contract</span>
                                <span className="font-medium">#{contractId}</span>
                            </div>
                            {isEditing && existingRecord && (
                                <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">{typeLabel} ID</span>
                                    <span className="font-medium">#{existingRecord.id}</span>
                                </div>
                            )}
                        </div>

                        {/* Week Picker */}
                        <div className="space-y-2">
                            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Period</Label>
                            <UniversalDateRangePicker
                                date={period}
                                onDateChange={setPeriod}
                                className="w-full"
                            />
                        </div>

                        {/* Reason */}
                        <div className="space-y-2">
                            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                Reason {type === 'note' && <span className="text-destructive">*</span>}
                            </Label>
                            <Textarea
                                placeholder={
                                    type === 'waiver'
                                        ? 'Optional: Explain why the shortfall is being waived...'
                                        : 'Enter the reason for this note...'
                                }
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                rows={4}
                            />
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="pt-4 border-t mt-4 flex gap-2">
                        {isEditing && (
                            <Button
                                variant="destructive"
                                size="icon"
                                onClick={() => setShowDeleteConfirm(true)}
                                disabled={isPending}
                            >
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        )}
                        <Button
                            variant="outline"
                            className="flex-1"
                            onClick={() => onOpenChange(false)}
                            disabled={isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            className="flex-1"
                            onClick={handleSave}
                            disabled={isPending}
                        >
                            {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            {isEditing ? 'Update' : 'Save'}
                        </Button>
                    </div>
                </SheetContent>
            </Sheet>

            {/* Delete Confirmation */}
            <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete {typeLabel}?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This action cannot be undone. The {typeLabel.toLowerCase()} for this period will be permanently removed.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
